import type { LessonDataSourceEnum } from "@/generated/graphql";
import type { SortableHandleProps } from "@/lib/dnd/components/Sortable";
import { Sortable } from "@/lib/dnd/components/Sortable";
import { useBookLessonSummaries } from "@/lib/hooks/bookLessons/useBookLessons";
import type { GenerationPreset } from "@/lib/hooks/lessonGenerator/useGenerationPresets";
import { useStartLessonGenerationBatch } from "@/lib/hooks/lessonGenerator/useLessonGeneration";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { Button, Modal } from "@ai-teach/ui-web";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useState } from "react";
import { HiOutlinePencil, HiOutlineTrash, HiPlus } from "react-icons/hi";
import { RxDragHandleDots2 } from "react-icons/rx";
import { BookLessonMultiPicker } from "./BookLessonMultiPicker";
import { type BookScope, EMPTY_BOOK_SCOPE } from "./BookScopePicker";
import { PresetBar } from "./PresetBar";
import { SectionEditorModal } from "./SectionEditorModal";
import { DATA_SOURCES, describeCount, describeParent } from "./countRules";
import { readsNothing } from "./types";
import type { GeneratorSection } from "./types";

function Step({
  number,
  title,
  hint,
  children,
}: {
  number: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-baseline gap-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-[11px] font-bold text-primary-500">
          {number}
        </span>
        <h3 className="card-title">{title}</h3>
      </div>
      {hint && <p className="pl-8 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
      <div className="pl-8">{children}</div>
    </section>
  );
}

function SectionSummary({
  sections,
  onReorder,
  onEdit,
  onRemove,
}: {
  sections: GeneratorSection[];
  onReorder: (sections: GeneratorSection[]) => void;
  onEdit: (section: GeneratorSection) => void;
  onRemove: (section: GeneratorSection) => void;
}) {
  if (sections.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center dark:border-gray-700">
        <p className="body-sm text-gray-500 dark:text-gray-400">
          Pick a preset, or add a section, to say what each lesson will hold.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Sortable
        items={sections}
        onReorder={(next) => onReorder(next)}
        handle
        strategy={verticalListSortingStrategy}
      >
        {(section, _index, dragHandle) => (
          <div
            className={`flex items-start gap-3 rounded-lg border border-gray-200 px-3.5 py-2.5 dark:border-gray-800 ${
              section.parent ? "opacity-50" : ""
            }`}
          >
            {dragHandle && (
              <button
                type="button"
                ref={dragHandle.ref}
                {...dragHandle.attributes}
                {...dragHandle.listeners}
                aria-label="Drag to reorder section"
                className="mt-0.5 shrink-0 cursor-grab text-gray-300 transition-colors hover:text-gray-500 active:cursor-grabbing dark:text-gray-600 dark:hover:text-gray-400"
              >
                <RxDragHandleDots2 className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="label truncate">
                  {section.name ?? getTemplateLabel(section.template)}
                </span>
                {section.name !== undefined && (
                  <span className="truncate text-[11px] text-gray-500">
                    {getTemplateLabel(section.template)}
                  </span>
                )}
                <span className="truncate font-mono text-[11px] text-gray-500">
                  ref: {section.referenceSlide.name}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {section.parent ? (
                  <>
                    {describeParent(section.parent, section.parent.tagId)} — skipped: a lesson a
                    batch creates has no main slides to attach to yet
                  </>
                ) : (
                  <>
                    {section.context.map((one) => DATA_SOURCES[one].label).join(", ") ||
                      "no context"}
                    {section.sectionScopes?.length ? ` in ${section.sectionScopes.join(", ")}` : ""}{" "}
                    · {section.count && describeCount(section.count)}
                  </>
                )}
              </p>
            </div>

            <div className="flex shrink-0 gap-0.5">
              {!section.parent && (
                <button
                  type="button"
                  onClick={() => onEdit(section)}
                  aria-label="Edit section"
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/5 dark:hover:text-gray-200"
                >
                  <HiOutlinePencil className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onRemove(section)}
                aria-label="Remove section"
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-error-500/10 hover:text-error-500"
              >
                <HiOutlineTrash className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </Sortable>
    </div>
  );
}

export function GenerateLessonsModal({
  isOpen,
  onClose,
  projectId,
  onStarted,
}: {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onStarted: () => void;
}) {
  const [scope, setScope] = useState<BookScope>(EMPTY_BOOK_SCOPE);
  const [sections, setSections] = useState<GeneratorSection[]>([]);
  const [bookLessonIds, setBookLessonIds] = useState<string[]>([]);
  const [editing, setEditing] = useState<GeneratorSection | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const { startBatch, starting } = useStartLessonGenerationBatch();
  const { bookLessons } = useBookLessonSummaries(scope.seriesSlug);

  // A batch has no one target lesson, so its earliest lesson stands for the book content shown.
  const sampleLesson =
    bookLessons
      .filter((one) => bookLessonIds.includes(one.id))
      .sort((a, b) => a.lesson - b.lesson)[0] ?? null;

  const applyPreset = (preset: GenerationPreset) => {
    setScope({
      seriesSlug: preset.target.seriesSlug,
      levelId: preset.target.levelId,
      componentId: preset.target.componentId,
    });
    setSections(preset.sections);
    setBookLessonIds([]);
  };

  const upsertSection = (section: GeneratorSection) => {
    setSections((prev) => {
      const index = prev.findIndex((one) => one.id === section.id);
      if (index === -1) return [...prev, section];
      return prev.map((one) => (one.id === section.id ? section : one));
    });
  };

  const openEditor = (section: GeneratorSection | null) => {
    setEditing(section);
    setIsEditorOpen(true);
  };

  const bookSections = sections.filter((section) => !section.parent);
  const emptySections = bookSections.filter(readsNothing);
  const canStart = bookSections.length > 0 && bookLessonIds.length > 0;

  const start = async () => {
    await startBatch({
      projectId,
      bookLessonIds,
      sections: bookSections.map((section) => ({
        id: section.id,
        template: section.template,
        referenceSlideId: section.referenceSlide.id,
        context: section.context as LessonDataSourceEnum[],
        sectionScopes: section.sectionScopes ?? [],
        subLists: section.subLists ?? [],
        count:
          section.count?.rule === "fixed"
            ? { rule: "fixed", total: section.count.total }
            : {
                rule: "perSourceItem",
                countFrom: section.count?.countFrom as LessonDataSourceEnum,
                each: section.count?.each ?? 1,
              },
        prompt: section.prompt,
        randomizeChoicePositions: section.randomizeChoicePositions ?? false,
        generateSentence: section.generateSentence ?? false,
        slideName: section.slideName,
        numberSlides: section.numberSlides,
      })),
    });
    onStarted();
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Generate Lessons" size="xl">
        <div className="space-y-7">
          <Step
            number={1}
            title="Sections"
            hint="Every generated lesson is built from these sections, in the order listed. Start from a preset, then add, tweak or drag sections for this batch."
          >
            <div className="space-y-3">
              <PresetBar
                target={{ ...scope, lesson: null }}
                sections={sections}
                autoApply={sections.length === 0}
                onApply={applyPreset}
              />
              <SectionSummary
                sections={sections}
                onReorder={setSections}
                onEdit={openEditor}
                onRemove={(section) =>
                  setSections((prev) => prev.filter((one) => one.id !== section.id))
                }
              />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<HiPlus className="h-4 w-4" />}
                onClick={() => openEditor(null)}
              >
                Add section
              </Button>
            </div>
          </Step>

          <Step
            number={2}
            title="Book lessons"
            hint="One lesson is created per book lesson you check, named after its number. Slide counts follow each lesson's own content."
          >
            <BookLessonMultiPicker
              scope={scope}
              selectedIds={bookLessonIds}
              onScopeChange={(next) => {
                setScope(next);
                setBookLessonIds([]);
              }}
              onSelectionChange={setBookLessonIds}
            />
          </Step>

          <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-800">
            <span
              className={`text-xs ${emptySections.length > 0 ? "text-warning-500" : "text-gray-500 dark:text-gray-400"}`}
            >
              {emptySections.length > 0
                ? `${emptySections.map((one) => one.name ?? one.referenceSlide.name).join(", ")}: nothing checked, written from the example slide alone.`
                : canStart
                  ? `${bookLessonIds.length} lesson${bookLessonIds.length === 1 ? "" : "s"} will be created, each with a draft to review.`
                  : "No slides are written — each lesson gets a draft you review first."}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!canStart} isLoading={starting} onClick={start}>
                Generate
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {isEditorOpen && (
        <SectionEditorModal
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSave={upsertSection}
          targetBook={null}
          bookScope={scope}
          sampleLesson={
            sampleLesson && { id: sampleLesson.id, printedLabel: sampleLesson.printedLabel }
          }
          {...(editing ? { section: editing } : {})}
        />
      )}
    </>
  );
}
